/**
 * ============================================================
 * LAB DAG — Grafo Dirigido Acíclico de Dependências de Domínio
 * ============================================================
 * Define as dependências de pré-requisito entre fluxos canônicos.
 * Permite:
 *   1. Execução isolada de qualquer fluxo (verificando pré-requisitos)
 *   2. Pipeline E2E completo em ordem topológica correta
 *   3. Descoberta do "próximo roteiro recomendado" ao concluir um fluxo
 *
 * REGRA DE NEGÓCIO:
 *   - Escola requer Rede + Estado + Município
 *   - Matrícula requer Escola configurada + Turmas + Professores
 *   - Chamada requer Alunos matriculados
 */

export interface FlowNode {
  id: string;
  /** IDs dos fluxos que DEVEM ter sido executados antes deste */
  dependsOn: string[];
  /** IDs dos fluxos que se tornam disponíveis APÓS este concluir */
  unlocks: string[];
  /** Nível topológico (0 = sem dependências) */
  level: number;
  /** Fase descritiva do pipeline E2E */
  phase: string;
}

/** Grafo completo de dependências canônicas */
export const LAB_DAG: Record<string, FlowNode> = {
  // ─── FASE 0: AUTH ───
  estadual_acesso: {
    id: 'estadual_acesso',
    dependsOn: [],
    unlocks: ['ativar_estado', 'ativar_municipio', 'ativar_diretor', 'ativar_professor'],
    level: 0,
    phase: 'FASE 0 - Autenticação',
  },

  // ─── FASE 1: FUNDAÇÃO MASTER ───
  cadastro_redes_ensino: {
    id: 'cadastro_redes_ensino',
    dependsOn: ['estadual_acesso'],
    unlocks: ['cadastro_de_estados'],
    level: 1,
    phase: 'FASE 1 - Fundação Master',
  },
  cadastro_de_estados: {
    id: 'cadastro_de_estados',
    dependsOn: ['cadastro_redes_ensino'],
    unlocks: ['cadastro_de_municipios', 'master_cadastro_estado'],
    level: 1,
    phase: 'FASE 1 - Fundação Master',
  },
  master_cadastro_estado: {
    id: 'master_cadastro_estado',
    dependsOn: ['estadual_acesso'],
    unlocks: ['ativar_estado'],
    level: 1,
    phase: 'FASE 1 - Fundação Master',
  },
  cadastro_de_municipios: {
    id: 'cadastro_de_municipios',
    dependsOn: ['cadastro_de_estados'],
    unlocks: ['cadastro_de_escolas', 'cadastro_do_gestor_do_municipio', 'municipio_cadastro_escola'],
    level: 2,
    phase: 'FASE 1 - Fundação Master',
  },
  cadastro_de_escolas: {
    id: 'cadastro_de_escolas',
    dependsOn: ['cadastro_de_municipios', 'cadastro_redes_ensino'],
    unlocks: ['cadastro_do_diretor_da_escola', 'config_inicial_escola'],
    level: 3,
    phase: 'FASE 1 - Fundação Master',
  },
  cadastro_do_diretor_da_escola: {
    id: 'cadastro_do_diretor_da_escola',
    dependsOn: ['cadastro_de_escolas'],
    unlocks: ['editar_dados_do_diretor_da_escola', 'ativar_diretor'],
    level: 4,
    phase: 'FASE 1 - Fundação Master',
  },
  editar_dados_do_diretor_da_escola: {
    id: 'editar_dados_do_diretor_da_escola',
    dependsOn: ['cadastro_do_diretor_da_escola'],
    unlocks: [],
    level: 5,
    phase: 'FASE 1 - Fundação Master',
  },
  cadastro_do_gestor_do_municipio: {
    id: 'cadastro_do_gestor_do_municipio',
    dependsOn: ['cadastro_de_municipios'],
    unlocks: ['editar_dados_do_gestor_municipal', 'ativar_municipio'],
    level: 3,
    phase: 'FASE 1 - Fundação Master',
  },
  editar_dados_do_gestor_municipal: {
    id: 'editar_dados_do_gestor_municipal',
    dependsOn: ['cadastro_do_gestor_do_municipio'],
    unlocks: [],
    level: 4,
    phase: 'FASE 1 - Fundação Master',
  },

  // ─── FASE 1 ONBOARDING: ATIVAÇÃO ───
  ativar_estado: {
    id: 'ativar_estado',
    dependsOn: ['master_cadastro_estado'],
    unlocks: ['estadual_dashboard', 'estadual_alunos', 'estadual_comunicados'],
    level: 2,
    phase: 'FASE 1 - Ativação de Perfis',
  },
  ativar_municipio: {
    id: 'ativar_municipio',
    dependsOn: ['cadastro_do_gestor_do_municipio'],
    unlocks: ['gestao_municipal', 'municipio_cadastro_escola', 'dados_financeiros'],
    level: 4,
    phase: 'FASE 1 - Ativação de Perfis',
  },
  ativar_diretor: {
    id: 'ativar_diretor',
    dependsOn: ['cadastro_do_diretor_da_escola'],
    unlocks: ['config_inicial_escola', 'gestao_evasao'],
    level: 5,
    phase: 'FASE 2 - Onboarding Escolar',
  },

  // ─── FASE 2: CONFIGURAÇÃO DA ESCOLA ───
  config_inicial_escola: {
    id: 'config_inicial_escola',
    dependsOn: ['ativar_diretor'],
    unlocks: ['cadastro_matriz_materias', 'grade_horaria', 'ativar_equipe'],
    level: 6,
    phase: 'FASE 2 - Configuração da Escola',
  },
  cadastro_matriz_materias: {
    id: 'cadastro_matriz_materias',
    dependsOn: ['config_inicial_escola'],
    unlocks: ['grade_horaria', 'lancamento_notas'],
    level: 7,
    phase: 'FASE 2 - Configuração da Escola',
  },
  grade_horaria: {
    id: 'grade_horaria',
    dependsOn: ['config_inicial_escola', 'cadastro_matriz_materias'],
    unlocks: ['cadastro_matriculas'],
    level: 8,
    phase: 'FASE 2 - Configuração da Escola',
  },

  // ─── FASE 3: EQUIPE ───
  ativar_equipe: {
    id: 'ativar_equipe',
    dependsOn: ['config_inicial_escola'],
    unlocks: ['documentos_secretaria', 'cadastro_matriculas'],
    level: 7,
    phase: 'FASE 3 - Equipe Escolar',
  },
  ativar_professor: {
    id: 'ativar_professor',
    dependsOn: ['config_inicial_escola'],
    unlocks: ['recursos_humanos', 'roteiro_chamada', 'lancamento_notas'],
    level: 7,
    phase: 'FASE 3 - Equipe Escolar',
  },
  recursos_humanos: {
    id: 'recursos_humanos',
    dependsOn: ['ativar_professor'],
    unlocks: [],
    level: 8,
    phase: 'FASE 3 - Equipe Escolar',
  },

  // ─── FASE 4: ALUNOS ───
  cadastro_matriculas: {
    id: 'cadastro_matriculas',
    dependsOn: ['grade_horaria', 'ativar_equipe'],
    unlocks: ['ficha_aluno_transferencia', 'roteiro_chamada', 'acompanhamento_familiar'],
    level: 9,
    phase: 'FASE 4 - Alunos e Matrículas',
  },
  ficha_aluno_transferencia: {
    id: 'ficha_aluno_transferencia',
    dependsOn: ['cadastro_matriculas'],
    unlocks: ['documentos_secretaria'],
    level: 10,
    phase: 'FASE 4 - Alunos e Matrículas',
  },

  // ─── FASE 5: OPERAÇÕES DIÁRIAS ───
  roteiro_chamada: {
    id: 'roteiro_chamada',
    dependsOn: ['cadastro_matriculas', 'ativar_professor'],
    unlocks: ['alerta_ocorrencia_pais', 'justificar_falta', 'lancamento_notas'],
    level: 10,
    phase: 'FASE 5 - Operações Diárias',
  },
  alerta_ocorrencia_pais: {
    id: 'alerta_ocorrencia_pais',
    dependsOn: ['roteiro_chamada'],
    unlocks: [],
    level: 11,
    phase: 'FASE 5 - Operações Diárias',
  },
  lancamento_notas: {
    id: 'lancamento_notas',
    dependsOn: ['ativar_professor', 'cadastro_matriz_materias'],
    unlocks: ['portal_aluno'],
    level: 11,
    phase: 'FASE 5 - Operações Diárias',
  },
  justificar_falta: {
    id: 'justificar_falta',
    dependsOn: ['roteiro_chamada'],
    unlocks: [],
    level: 11,
    phase: 'FASE 5 - Operações Diárias',
  },
  documentos_secretaria: {
    id: 'documentos_secretaria',
    dependsOn: ['ativar_equipe', 'cadastro_matriculas'],
    unlocks: [],
    level: 10,
    phase: 'FASE 5 - Operações Diárias',
  },
  contingencia_noc: {
    id: 'contingencia_noc',
    dependsOn: ['config_inicial_escola'],
    unlocks: [],
    level: 7,
    phase: 'FASE 5 - Operações Diárias',
  },

  // ─── FASE 6: ANALYTICS ───
  gestao_evasao: {
    id: 'gestao_evasao',
    dependsOn: ['cadastro_matriculas', 'roteiro_chamada'],
    unlocks: [],
    level: 12,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_alunos: {
    id: 'estadual_alunos',
    dependsOn: ['ativar_estado', 'cadastro_matriculas'],
    unlocks: [],
    level: 12,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_chamadas: {
    id: 'estadual_chamadas',
    dependsOn: ['ativar_estado', 'roteiro_chamada'],
    unlocks: [],
    level: 12,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_comunicados: {
    id: 'estadual_comunicados',
    dependsOn: ['ativar_estado'],
    unlocks: [],
    level: 3,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_dashboard: {
    id: 'estadual_dashboard',
    dependsOn: ['ativar_estado'],
    unlocks: [],
    level: 3,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_escolas: {
    id: 'estadual_escolas',
    dependsOn: ['ativar_estado', 'cadastro_de_escolas'],
    unlocks: [],
    level: 5,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_municipios: {
    id: 'estadual_municipios',
    dependsOn: ['ativar_estado', 'cadastro_de_municipios'],
    unlocks: [],
    level: 4,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_usuarios: {
    id: 'estadual_usuarios',
    dependsOn: ['ativar_estado'],
    unlocks: [],
    level: 3,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_configuracoes: {
    id: 'estadual_configuracoes',
    dependsOn: ['ativar_estado'],
    unlocks: [],
    level: 3,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_perfil: {
    id: 'estadual_perfil',
    dependsOn: ['ativar_estado'],
    unlocks: [],
    level: 3,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estadual_ead: {
    id: 'estadual_ead',
    dependsOn: ['ativar_estado'],
    unlocks: [],
    level: 3,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  estado_cadastro_municipio: {
    id: 'estado_cadastro_municipio',
    dependsOn: ['ativar_estado', 'cadastro_de_municipios'],
    unlocks: [],
    level: 4,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  gestao_municipal: {
    id: 'gestao_municipal',
    dependsOn: ['ativar_municipio'],
    unlocks: [],
    level: 5,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  municipio_cadastro_escola: {
    id: 'municipio_cadastro_escola',
    dependsOn: ['ativar_municipio'],
    unlocks: [],
    level: 5,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  dados_financeiros: {
    id: 'dados_financeiros',
    dependsOn: ['ativar_municipio'],
    unlocks: [],
    level: 5,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  acompanhamento_familiar: {
    id: 'acompanhamento_familiar',
    dependsOn: ['cadastro_matriculas'],
    unlocks: ['justificar_falta'],
    level: 10,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  portal_aluno: {
    id: 'portal_aluno',
    dependsOn: ['cadastro_matriculas', 'lancamento_notas'],
    unlocks: ['trilha_gamificada'],
    level: 12,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
  trilha_gamificada: {
    id: 'trilha_gamificada',
    dependsOn: ['portal_aluno'],
    unlocks: [],
    level: 13,
    phase: 'FASE 6 - Analytics e Monitoramento',
  },
};

/**
 * Retorna a ordem topológica do DAG para execução do pipeline E2E completo.
 * Algoritmo: Kahn's Algorithm (BFS topological sort).
 */
export function getPipelineOrder(): string[] {
  const inDegree: Record<string, number> = {};
  const adjList: Record<string, string[]> = {};

  // Inicializar
  for (const id of Object.keys(LAB_DAG)) {
    inDegree[id] = 0;
    adjList[id] = [];
  }

  // Construir grafo de arestas a partir de dependsOn
  for (const [id, node] of Object.entries(LAB_DAG)) {
    for (const dep of node.dependsOn) {
      if (adjList[dep]) {
        adjList[dep].push(id);
        inDegree[id] = (inDegree[id] || 0) + 1;
      }
    }
  }

  // BFS
  const queue: string[] = Object.keys(inDegree).filter(id => inDegree[id] === 0);
  const result: string[] = [];

  while (queue.length > 0) {
    // Ordenar por level para execução em ordem lógica
    queue.sort((a, b) => (LAB_DAG[a]?.level ?? 0) - (LAB_DAG[b]?.level ?? 0));
    const current = queue.shift()!;
    result.push(current);

    for (const neighbor of adjList[current] || []) {
      inDegree[neighbor]--;
      if (inDegree[neighbor] === 0) {
        queue.push(neighbor);
      }
    }
  }

  return result;
}

/**
 * Retorna os IDs dos fluxos que desbloqueiam após a conclusão do fluxo dado.
 */
export function getUnlockedFlows(flowId: string): string[] {
  return LAB_DAG[flowId]?.unlocks ?? [];
}

/**
 * Retorna os IDs dos fluxos que são pré-requisito para o fluxo dado.
 */
export function getDependencies(flowId: string): string[] {
  return LAB_DAG[flowId]?.dependsOn ?? [];
}

/**
 * Agrupa os fluxos por fase do pipeline E2E.
 */
export function getFlowsByPhase(): Record<string, string[]> {
  const phases: Record<string, string[]> = {};
  for (const [id, node] of Object.entries(LAB_DAG)) {
    if (!phases[node.phase]) phases[node.phase] = [];
    phases[node.phase].push(id);
  }
  return phases;
}
